import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const SectionContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#f8f8f8',
  padding: '80px 0',
  position: 'relative',
  // Responsive padding
  [theme.breakpoints.down('xl')]: {
    padding: '70px 0'
  },
  [theme.breakpoints.down('lg')]: {
    padding: '60px 0'
  },
  [theme.breakpoints.down('md')]: {
    padding: '50px 0'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '40px 0'
  },
  '@media (max-width: 480px)': {
    padding: '30px 0'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    padding: '100px 0'
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: '#B4A082',
  fontWeight: 400,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  fontSize: '0.9rem',
  marginBottom: '24px',
  textAlign: 'center',
  // Responsive font sizes
  [theme.breakpoints.down('lg')]: {
    fontSize: '0.85rem',
    marginBottom: '20px'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '0.8rem',
    marginBottom: '18px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.75rem',
    marginBottom: '16px',
    letterSpacing: '0.15em'
  },
  '@media (max-width: 480px)': {
    fontSize: '0.7rem',
    marginBottom: '14px'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    fontSize: '1rem',
    marginBottom: '28px'
  }
}));

const MainHeading = styled(Typography)(({ theme }) => ({
  color: '#2c2c2c',
  fontWeight: 300,
  lineHeight: 1.2,
  marginBottom: '60px',
  fontSize: '1.5rem',
  letterSpacing: '1px',
  fontFamily: '"Playfair Display", serif',
  textAlign: 'center',
  // Responsive font sizes
  [theme.breakpoints.up('sm')]: {
    fontSize: '1.8rem',
    marginBottom: '50px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '2.2rem',
    marginBottom: '55px'
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '2.5rem',
    marginBottom: '60px'
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '3rem'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    fontSize: '3.5rem',
    marginBottom: '70px'
  },
  // Small mobile
  '@media (max-width: 480px)': {
    fontSize: '1.3rem',
    marginBottom: '35px',
    lineHeight: 1.3,
    padding: '0 10px'
  }
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  backgroundColor: 'transparent',
  marginBottom: '16px',
  borderRadius: '0 !important',
  boxShadow: 'none',
  border: 'none',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: '0 0 16px 0',
  },
  // Responsive spacing
  [theme.breakpoints.down('sm')]: {
    marginBottom: '12px',
    '&.Mui-expanded': {
      margin: '0 0 12px 0',
    },
  },
  '@media (max-width: 480px)': {
    marginBottom: '10px',
    '&.Mui-expanded': {
      margin: '0 0 10px 0',
    },
  }
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  backgroundColor: '#6c8c68',
  minHeight: '50px',
  padding: '0 24px',
  borderRadius: '25px',
  marginBottom: '8px',
  boxShadow: '0 4px 12px rgba(108, 140, 104, 0.2)',
  transition: 'all 0.3s ease',
  '&.Mui-expanded': {
    backgroundColor: '#455b40',
    boxShadow: '0 6px 20px rgba(69, 91, 64, 0.3)',
  },
  '&:hover': {
    backgroundColor: '#5a7862',
    '.Mui-expanded &': {
      backgroundColor: '#3f5139',
    },
  },
  '& .MuiAccordionSummary-content': {
    margin: '12px 0',
    alignItems: 'center',
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    color: 'white',
    transition: 'transform 0.3s ease, color 0.3s ease',
    '&.Mui-expanded': {
      transform: 'rotate(180deg)',
      color: 'rgba(255,255,255,0.9)',
    },
  },
  // Responsive sizing
  [theme.breakpoints.down('lg')]: {
    minHeight: '48px',
    padding: '0 22px'
  },
  [theme.breakpoints.down('md')]: {
    minHeight: '46px',
    padding: '0 20px',
    borderRadius: '23px'
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: '44px',
    padding: '0 18px',
    borderRadius: '22px',
    marginBottom: '6px',
    '& .MuiAccordionSummary-content': {
      margin: '10px 0',
    },
  },
  '@media (max-width: 480px)': {
    minHeight: '42px',
    padding: '0 16px',
    borderRadius: '21px',
    marginBottom: '5px',
    '& .MuiAccordionSummary-content': {
      margin: '8px 0',
    },
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    minHeight: '55px',
    padding: '0 28px',
    borderRadius: '28px'
  }
}));

const QuestionText = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  fontWeight: 600,
  color: 'white',
  fontFamily: '"Lora", serif',
  lineHeight: 1.4,
  // Responsive font sizes
  [theme.breakpoints.down('lg')]: {
    fontSize: '1.05rem'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '1rem'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.95rem',
    lineHeight: 1.3
  },
  '@media (max-width: 480px)': {
    fontSize: '0.9rem',
    lineHeight: 1.25
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    fontSize: '1.2rem'
  }
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: '24px',
  backgroundColor: 'white',
  marginTop: '0',
  marginBottom: '8px',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  border: '1px solid #f0f0f0',
  // Responsive padding
  [theme.breakpoints.down('lg')]: {
    padding: '22px'
  },
  [theme.breakpoints.down('md')]: {
    padding: '20px',
    borderRadius: '11px'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '18px',
    borderRadius: '10px',
    marginBottom: '6px'
  },
  '@media (max-width: 480px)': {
    padding: '16px',
    borderRadius: '9px',
    marginBottom: '5px'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    padding: '28px',
    borderRadius: '14px'
  }
}));

const AnswerText = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: '#666',
  lineHeight: 1.7,
  fontFamily: '"Lora", serif',
  '& ul': {
    paddingLeft: '20px',
    marginTop: '12px',
  },
  '& li': {
    marginBottom: '8px',
  },
  // Responsive font sizes
  [theme.breakpoints.down('lg')]: {
    fontSize: '0.95rem'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '0.9rem',
    lineHeight: 1.65,
    '& ul': {
      paddingLeft: '18px',
      marginTop: '10px',
    },
    '& li': {
      marginBottom: '6px',
    }
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.85rem',
    lineHeight: 1.6,
    '& ul': {
      paddingLeft: '16px',
      marginTop: '8px',
    },
    '& li': {
      marginBottom: '5px',
    }
  },
  '@media (max-width: 480px)': {
    fontSize: '0.8rem',
    lineHeight: 1.55,
    '& ul': {
      paddingLeft: '14px',
      marginTop: '6px',
    },
    '& li': {
      marginBottom: '4px',
    }
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    fontSize: '1.1rem',
    lineHeight: 1.75,
    '& ul': {
      paddingLeft: '24px',
      marginTop: '14px',
    },
    '& li': {
      marginBottom: '10px',
    }
  }
}));

const faqData = [
  {
    question: "¿Por qué elegir alimentos ecológicos?",
    answer: "Los alimentos ecológicos están libres de pesticidas y químicos de síntesis, lo que los hace más saludables y respetuosos con el medio ambiente. Además, cultivados de manera responsable, pueden venderse a precios similares a los convencionales, garantizando accesibilidad y salud preventiva."
  },
  {
    question: "¿Qué tipo de productos ofrece Ekkofood?",
    answer: "Cultivamos hortalizas de temporada y granadas ecológicas principalmente. Contamos con más de 500 árboles de granado ecológico y tenemos una superficie total de 3000m2. Nuestra producción está certificada como ecológica y se distribuye de forma local y directa."
  },
  {
    question: "¿Qué cursos ofrece Ekkofood?",
    answer: `En Ekkofood organizamos talleres y cursos de agricultura ecológica con expertos nacionales e internacionales. Los temas incluyen:
    <ul>
      <li>Producción ecológica accesible</li>
      <li>Elaboración de biofertilizantes y biopesticidas</li>
      <li>Control de plagas y enfermedades de forma natural</li>
      <li>Permacultura y regeneración del suelo</li>
    </ul>`
  },
  {
    question: "¿Qué significa \"La salud viene del suelo\"?",
    answer: "Creemos que la base de la salud humana es la salud del suelo. Los microorganismos, minerales y procesos naturales del suelo sano permiten que las plantas crezcan fuertes y nutritivas, lo que se traduce en alimentos de calidad que previenen enfermedades."
  },
  {
    question: "¿Cómo puedo empezar a cultivar de manera ecológica?",
    answer: "Puedes aprender a cultivar de manera ecológica siguiendo nuestras publicaciones en el <a href='/noticias-blog' style='color: #6c8c68; font-weight: 600; text-decoration: underline;'>blog</a> y asistiendo a nuestros <a href='/cursos' style='color: #6c8c68; font-weight: 600; text-decoration: underline;'>cursos de agricultura orgánica</a>. En ellos enseñamos métodos prácticos y accesibles para producir alimentos sin químicos, mejorando el suelo y la salud de las personas."
  }
];

const FAQSection: React.FC = () => {
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <SectionContainer>
      <Container maxWidth="md" sx={{ 
        px: { xs: 2, sm: 3, md: 4 },
        '@media (max-width: 480px)': {
          px: 1.5
        }
      }}>
        <SectionTitle>
          Preguntas Frecuentes
        </SectionTitle>
        <MainHeading>
          Preguntas frecuentes sobre Ekkofood, agricultura ecológica y alimentación saludable
        </MainHeading>
        
        <Box sx={{ 
          maxWidth: { xs: '100%', sm: '100%', md: '100%' },
          '@media (min-width: 1920px)': {
            maxWidth: '1000px',
            mx: 'auto'
          }
        }}>
          {faqData.map((faq, index) => (
            <StyledAccordion
              key={index}
              expanded={expanded === `panel${index}`}
              onChange={handleChange(`panel${index}`)}
            >
              <StyledAccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel${index}-content`}
                id={`panel${index}-header`}
              >
                <QuestionText>
                  {faq.question}
                </QuestionText>
              </StyledAccordionSummary>
              <StyledAccordionDetails>
                <AnswerText 
                  dangerouslySetInnerHTML={{ __html: faq.answer }}
                />
              </StyledAccordionDetails>
            </StyledAccordion>
          ))}
        </Box>
      </Container>
    </SectionContainer>
  );
};

export default FAQSection;